import React from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useInView} from '../../../hooks/useInView/useInView';
import {Button} from '../../atoms/Buttons/Button';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';

export const ProductTileEndOfStreamWithCTA: React.FC<{
  headline: string;
  subline?: string;
  cta: {link: {label: string; linkTarget: {url: string; external: boolean}}};
  onCTAClick?: VoidFunction;
}> = ({headline, subline, cta, onCTAClick}) => {
  const {ref, inView} = useInView<HTMLDivElement>({
    threshold: 1,
    triggerOnce: true,
  });

  return (
    <ProductTileEndOfStreamWrapper ref={ref}>
      <IconAnimationWrapper $shouldAnimate={inView}>
        <AnimatedIcon
          $shouldAnimate={inView}
          asset={GenericIconName.AlertSuccess}
          size="L"
        />
      </IconAnimationWrapper>
      <AnimatedDescription $shouldAnimate={inView}>
        {headline}
      </AnimatedDescription>
      <AnimatedSubline $shouldAnimate={inView}>{subline}</AnimatedSubline>
      <StyledButton
        variant="secondary"
        onClick={onCTAClick}
        href={cta.link.linkTarget.url}
        openExternalLinkInNewTab={cta.link.linkTarget.external}
      >
        {cta.link.label}
      </StyledButton>
    </ProductTileEndOfStreamWrapper>
  );
};

const ProductTileEndOfStreamWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1rem 1rem;
`;

const IconAnimationWrapper = styled.div<{$shouldAnimate: boolean}>`
  position: relative;
  &:after {
    content: '';
    position: absolute;
    transform: translateX(
        ${({$shouldAnimate}) => ($shouldAnimate ? '1rem' : '0.5rem')}
      )
      scale(${({$shouldAnimate}) => ($shouldAnimate ? '0' : '1')});
    top: 0.5rem;
    background-color: ${COLORS.White};
    transition: transform 0.3s;
    transition-delay: 0.3s;
  }
`;

const AnimatedIcon = styled(Icon)<{$shouldAnimate: boolean}>`
  position: relative;
  opacity: ${({$shouldAnimate}) => ($shouldAnimate ? '1' : '0')};
  transform: scale(${({$shouldAnimate}) => ($shouldAnimate ? '1' : '0')});
  transition: opacity, transform 0.3s;
`;

const AnimatedDescription = styled.p<{$shouldAnimate: boolean}>`
  position: relative;
  color: ${COLORS.Black};
  margin-top: 0.75rem;
  top: 0.3125rem;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.bold};
  text-align: center;
  min-height: 1.875rem;
  width: 100%;
  opacity: ${({$shouldAnimate}) => ($shouldAnimate ? '1' : '0')};
  transform: translateY(
    ${({$shouldAnimate}) => ($shouldAnimate ? '0' : '0.3125rem')}
  );
  transition: opacity, transform 0.3s;
`;

const AnimatedSubline = styled.p<{$shouldAnimate: boolean}>`
  position: relative;
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  text-align: center;
  min-height: 1.875rem;
  width: 100%;
  opacity: ${({$shouldAnimate}) => ($shouldAnimate ? '1' : '0')};
  transform: translateY(
    ${({$shouldAnimate}) => ($shouldAnimate ? '0' : '0.3125rem')}
  );
  transition: opacity, transform 0.3s;
`;

const StyledButton = styled(Button)`
  &&& {
    padding: 14px 32px;
  }
`;
